// Typed client for /api/ForexTrading/*. Mirrors the controller's DTOs.
// Follows the project pattern: API_BASE + apiFetch (auth + base URL come
// from there); no shared "ApiService" object.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export interface FxcmHealth {
  enabled: boolean;
  brokerReachable: boolean;
  accountId: string | null;
  message: string;
}

export interface FxcmSettings {
  username: string;
  passwordMasked: string;       // "**** set" | "**** unset"
  connectionType: 'Demo' | 'Real';
  tradingServerUrl: string;
  accountId: string;
  tradingEnabled: boolean;
  updatedAt: string | null;
}

export interface FxcmAccount {
  accountId: string;
  status: string;
  balance: number;
  equity: number;
  usedMargin: number;
  usableMargin: number;
  marginLevelPercent: number | null;
  isLocked: boolean;
}

export interface FxcmPosition {
  symbol: string;       // internal form, e.g. EURUSD
  side: 'long' | 'short';
  amount: number;       // contract units (NOT lots — UI converts at display)
  openRate: number;
  currentRate: number | null;
  unrealizedPnL: number | null;
  unrealizedPnLPercent: number | null;
}

export interface FxcmOrder {
  orderId: string;
  clientOrderId: string;
  symbol: string;
  side: string;
  amount: number;
  status: string;
  rate: number;
  submittedAt: string | null;
  filledAt: string | null;
}

export interface UpdateFxcmSettings {
  username?: string;
  password?: string;       // blank preserves existing
  tradingServerUrl?: string;
  connectionType?: 'Demo' | 'Real';
  accountId?: string;
  tradingEnabled?: boolean;
}

export interface PlaceMarketOrder {
  symbol: string;
  side: 'buy' | 'sell' | 'long' | 'short';
  lots: number;
  stopPrice?: number;   // optional attached stop-loss rate
  limitPrice?: number;  // optional attached take-profit rate
}

export interface FxcmInstrument {
  symbol: string;             // internal no-slash form, e.g. EURUSD / XAUUSD
  displaySymbol: string;      // FXCM form, e.g. EUR/USD / XAU/USD / US30
  instrumentClass: string;    // forex | indices | commodity | bullion | crypto | …
  contractMultiplier: number; // lots → contract units
  pointSize: number;          // smallest price increment
  isTradingAllowed: boolean;
}

export interface FxcmQuote {
  symbol: string;             // internal form, e.g. EURUSD
  bid: number;
  ask: number;
  spread: number;             // ask - bid, in raw rate units
  timestampUtc: string;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<T>;
}

async function sendJson<T>(path: string, method: 'POST' | 'PUT', body?: unknown): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  // Some endpoints return 204 / empty — guard against JSON parse failure.
  const text = await res.text();
  return (text ? JSON.parse(text) : ({} as T)) as T;
}

// Demo and live forex are the SAME API shape on two different controllers
// (api/ForexTrading vs api/ForexLiveTrading). createForexService binds the
// client to one route surface; the panel takes whichever it needs as a prop
// so a demo panel can never accidentally hit the live (real-money) account.
function createForexService(apiBase: string) {
  return {
    health: () => getJson<FxcmHealth>(`${apiBase}/health`),
    getSettings: () => getJson<FxcmSettings>(`${apiBase}/settings`),
    updateSettings: (req: UpdateFxcmSettings) =>
      sendJson<FxcmSettings>(`${apiBase}/settings`, 'PUT', req),
    setEnabled: (tradingEnabled: boolean) =>
      sendJson<FxcmSettings>(`${apiBase}/config`, 'PUT', { tradingEnabled }),
    getAccount: () => getJson<FxcmAccount>(`${apiBase}/account`),
    getPositions: () => getJson<FxcmPosition[]>(`${apiBase}/positions`),
    closePosition: (symbol: string) =>
      sendJson<{ message: string }>(
        `${apiBase}/positions/${encodeURIComponent(symbol)}/close`, 'POST'),
    getRecentOrders: (limit = 50) =>
      getJson<FxcmOrder[]>(`${apiBase}/orders/recent?limit=${limit}`),
    getInstruments: () => getJson<FxcmInstrument[]>(`${apiBase}/instruments`),
    getQuotes: () => getJson<FxcmQuote[]>(`${apiBase}/quotes`),
    placeMarketOrder: (req: PlaceMarketOrder) =>
      sendJson<{ orderId: string; symbol: string; side: string; lots: number }>(
        `${apiBase}/orders/market`, 'POST', req),
  };
}

/** Shared shape of the demo / live forex clients. */
export type ForexService = ReturnType<typeof createForexService>;

/** Demo — FXCM practice account. Drives the Paper Trading → Forex tab. */
export const forexService = createForexService('/api/ForexTrading');

/** Live — funded FXCM account. REAL MONEY. Drives the Live Trading page. */
export const forexLiveService = createForexService('/api/ForexLiveTrading');

/** Human label for an instrument-class code from the API. */
export function instrumentClassLabel(c: string): string {
  switch (c) {
    case 'forex': return 'Forex';
    case 'indices': return 'Indices';
    case 'commodity': return 'Commodities';
    case 'bullion': return 'Metals';
    case 'crypto': return 'Crypto';
    case 'treasury': return 'Treasuries';
    case 'shares': case 'cfdshares': return 'Shares';
    case 'fxindex': return 'FX Index';
    default: return 'Other';
  }
}

// ── Display helpers ─────────────────────────────────────────────

/** Format an internal forex symbol (EURUSD) with the standard slash for
 * display (EUR/USD). Equity tickers and unknowns return unchanged. */
export function fmtForexSymbol(s: string): string {
  if (!s || s.length !== 6) return s;
  return `${s.slice(0, 3)}/${s.slice(3, 6)}`;
}

/** Standard contract sizes per pair family. JPY pairs: 1 lot = 1,000
 * contracts. Other pairs: 1 lot = 100,000 contracts. Mini = 0.1 lot,
 * micro = 0.01 lot. Used to convert FXCM's contract-unit `amount` field
 * back into the lots the trader entered. */
export function contractToLots(symbol: string, amount: number): number {
  const upper = symbol.toUpperCase();
  const isJpy = upper.includes('JPY');
  const multiplier = isJpy ? 1000 : 100_000;
  return amount / multiplier;
}

/** Inverse of {@link contractToLots}. */
export function lotsToContracts(symbol: string, lots: number): number {
  const upper = symbol.toUpperCase();
  const isJpy = upper.includes('JPY');
  const multiplier = isJpy ? 1000 : 100_000;
  return Math.round(lots * multiplier);
}
